import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMessageCircle, FiSend, FiX, FiBot, FiMic, FiMicOff, FiThumbsUp, FiThumbsDown, FiCopy } = FiIcons;

const AIAssistant = ({ property, guestPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: `Hi! I'm your AI assistant for ${property.name}. I can help you with appliances, house rules, and local information. What would you like to know?`,
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [suggestions, setSuggestions] = useState([
    "What's the WiFi password?",
    "How do I use the TV?",
    "Where are the house rules?",
    "Local restaurants nearby?"
  ]);
  const messagesEndRef = useRef(null);

  const { allAppliances, allFaqs } = useData();

  const propertyAppliances = allAppliances.filter(a => a.propertyId === property.id);
  const propertyFaqs = allFaqs.filter(f => f.propertyId === property.id);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fallback response generator (used if OpenAI fails)
  const generateFallbackResponse = (question) => {
    const lowerQuestion = question.toLowerCase();

    // Check FAQs first
    const matchingFaq = propertyFaqs.find(faq => 
      lowerQuestion.includes(faq.question.toLowerCase().split(' ').slice(0, 3).join(' '))
    );
    if (matchingFaq) {
      return matchingFaq.answer;
    }

    // Check appliances
    const matchingAppliance = propertyAppliances.find(appliance => 
      lowerQuestion.includes(appliance.type.toLowerCase()) || 
      lowerQuestion.includes(appliance.brand?.toLowerCase()) || 
      lowerQuestion.includes(appliance.location?.toLowerCase())
    );
    if (matchingAppliance) {
      return `Here's information about the ${matchingAppliance.type}:\n\n` +
        `Location: ${matchingAppliance.location}\n` +
        `Brand/Model: ${matchingAppliance.brand} ${matchingAppliance.model}\n\n` +
        `Instructions:\n${matchingAppliance.instructions}\n\n` +
        (matchingAppliance.troubleshooting ? `Troubleshooting:\n${matchingAppliance.troubleshooting}\n\n` : '') +
        (matchingAppliance.safetyNotes ? `Safety Notes:\n${matchingAppliance.safetyNotes}` : '');
    }

    // Property-specific responses
    if (lowerQuestion.includes('wifi') || lowerQuestion.includes('internet')) {
      return `WiFi Information:\nNetwork: ${property.wifiName || 'Not provided'}\nPassword: ${property.wifiPassword || 'Contact host'}`;
    }
    if (lowerQuestion.includes('checkout') || lowerQuestion.includes('check out')) {
      return property.checkoutProcedures || 'Please refer to the checkout procedures in your portal or contact the host.';
    }
    if (lowerQuestion.includes('rules') || lowerQuestion.includes('rule')) {
      return property.houseRules || 'Please refer to the house rules section in your portal.';
    }
    if (lowerQuestion.includes('tv') || lowerQuestion.includes('television')) {
      return property.tvInstructions || 'TV instructions are not available. Please contact the host for assistance.';
    }
    if (lowerQuestion.includes('fireplace') || lowerQuestion.includes('fire')) {
      return property.fireplaceInstructions || 'Fireplace instructions are not available. Please contact the host for assistance.';
    }
    if (lowerQuestion.includes('local') || lowerQuestion.includes('restaurant') || lowerQuestion.includes('food')) {
      return property.localRecommendations || 'Local recommendations are available in your portal. Check the Local Recommendations section.';
    }
    if (lowerQuestion.includes('contact') || lowerQuestion.includes('help') || lowerQuestion.includes('support')) {
      return property.supportContact || 'For immediate assistance, please check your portal for contact information.';
    }

    // Default response
    return "I'd be happy to help! I can assist you with:\n\n" +
      "• Appliance instructions (TV, fireplace, etc.)\n" +
      "• WiFi and internet access\n" +
      "• House rules and checkout procedures\n" +
      "• Local recommendations\n" +
      "• Contact information\n\n" +
      "What specific information do you need?";
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      isBot: false,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputText;
    setInputText('');
    setIsTyping(true);

    try {
      // Import AI chat API
      const { aiChatAPI } = await import('../../services/api');
      
      // Build conversation history (last 10 messages for context)
      const conversationHistory = messages
        .slice(-10)
        .map(msg => ({
          text: msg.text,
          isBot: msg.isBot
        }));

      // Try OpenAI API first
      if (guestPage?.slug) {
        try {
          const response = await aiChatAPI.sendMessage(
            guestPage.slug,
            currentMessage,
            conversationHistory
          );

          if (response.success && response.message) {
            const botMessage = {
              id: Date.now() + 1,
              text: response.message,
              isBot: true,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, botMessage]);
            setIsTyping(false);
            return;
          }
        } catch (apiError) {
          console.warn('OpenAI API error, falling back to local response:', apiError);
          // Fall through to fallback
        }
      }
      
      // Fallback to local response generator
      setTimeout(() => {
        const response = generateFallbackResponse(currentMessage);
        const botMessage = {
          id: Date.now() + 1,
          text: response,
          isBot: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
      }, 500 + Math.random() * 1000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Fallback to local response
      setTimeout(() => {
        const response = generateFallbackResponse(currentMessage);
        const botMessage = {
          id: Date.now() + 1,
          text: response,
          isBot: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
      }, 500);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputText(suggestion);
    handleSendMessage();
  };

  const handleVoiceRecording = () => {
    setIsRecording(!isRecording);
    // Voice recording functionality would go here
    setTimeout(() => {
      setIsRecording(false);
      setInputText("What's the WiFi password?");
    }, 2000);
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center z-40 ${isOpen ? 'hidden' : 'flex'}`}
      >
        <SafeIcon icon={FiMessageCircle} className="text-2xl" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 w-[400px] h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50"
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiBot} className="text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold">Property Assistant</h3>
                  <p className="text-xs text-blue-100 flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                    Always here to help
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors duration-200"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`max-w-xs relative group ${message.isBot ? '' : 'ml-auto'}`}>
                    <div className={`px-4 py-2 rounded-2xl ${
                      message.isBot
                        ? 'bg-gray-100 text-gray-800 rounded-tl-none'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-tr-none'
                    }`}>
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                    </div>
                    
                    {/* Message Actions */}
                    <div className={`absolute top-0 ${message.isBot ? 'left-full ml-2' : 'right-full mr-2'} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => copyMessage(message.text)}
                          className="p-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors duration-200"
                          title="Copy message"
                        >
                          <SafeIcon icon={FiCopy} className="text-xs text-gray-600" />
                        </button>
                        <button
                          className="p-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors duration-200"
                          title="Good response"
                        >
                          <SafeIcon icon={FiThumbsUp} className="text-xs text-gray-600" />
                        </button>
                        <button
                          className="p-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors duration-200"
                          title="Bad response"
                        >
                          <SafeIcon icon={FiThumbsDown} className="text-xs text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-tl-none">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {messages.length === 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-gray-500 mb-2">Suggested questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs transition-colors duration-200"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-gray-100">
              <div className="flex items-end space-x-2">
                <button
                  onClick={handleVoiceRecording}
                  className={`p-2 rounded-lg transition-colors duration-200 ${
                    isRecording
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <SafeIcon icon={isRecording ? FiMicOff : FiMic} className="text-sm" />
                </button>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about appliances, rules, or local info..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  rows={1}
                  style={{ maxHeight: '80px' }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim()}
                  className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  <SafeIcon icon={FiSend} className="text-sm" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;